/**
 * The salary wire shapes — `Salary`, the calculator bodies and `SalaryRunResult` from
 * `docs/api/paths/salaries.yaml` — and the row type the table renders.
 * Pure: safe to import from client components.
 */

import { EntryStatusTypeValue, strEnum } from '@/lib/api/types'
import type { UserSalaryAttributesType } from '@/lib/api/users/types'

export const KtgType = strEnum(['men30', 'men60', 'women30', 'women60'])
export type KtgValue = keyof typeof KtgType

/** The booking types of a salary's legs; the contract names them only inside `BookingType`. */
export const SalaryType = strEnum([
  'salary_gross', // Bruttolohn
  'salary_net', // Nettolohn
  'salary_social_insurance_employer_part', // Sozialversicherung Arbeitgeberanteil
  'salary_total_deductions_employer_part', // Total Abzüge Arbeitgeberanteil
  'salary_total_deductions_employee_part', // Total Abzüge Arbeitnehmeranteil
  'salary_ktg', // Krankentaggeld
  'salary_gross_bonus', // Bruttolohn Bonus
  'salary_net_bonus', // Nettolohn Bonus
])
export type SalaryTypeValue = keyof typeof SalaryType

/** The payroll engine's breakdown of one salary (`Salary.calculated`). */
export type CalculatedType = {
  ahvIvEoAnUngerundet: number
  ahvIvEoAn: number
  ahvIvEoAnFormel: string
  ahvIvEoAgUngerundet: number
  ahvIvEoAg: number
  ahvIvEoAgFormel: string
  alvAnUngerundet: number
  alvAn: number
  alvAnFormel: string
  alvAgUngerundet: number
  alvAg: number
  alvAgFormel: string
  alv2AnUngerundet?: number
  alv2An?: number
  alv2AnFormel?: string
  alv2AgUngerundet?: number
  alv2Ag?: number
  alv2AgFormel?: string
  nbuAnUngerundet: number
  nbuAn: number
  nbuAnFormel: string
  ahvVerwaltungAgUngerundet: number
  ahvVerwaltungAg: number
  ahvVerwaltungAgFormel: string
  buAgUngerundet: number
  buAg: number
  buAgFormel: string
  fakAgUngerundet: number
  fakAg: number
  fakAgFormel: string
  ktgAnUngerundet: number
  ktgAn: number
  ktgAgUngerundet: number
  ktgAg: number
  totalAbzuegeAnUngerundet: number
  totalAbzuegeAn: number
  totalAbzuegeAgUngerundet: number
  totalAbzuegeAg: number
  nettoUngerundet: number
  netto: number
  auszahlbarUngerundet: number
  auszahlbar: number
}

export type ApiSalary = {
  id: number
  /** `YYYY-MM`; month 13 is the bonus. */
  yearMonth: string
  issued: string
  receiver: number
  /** `Money`: fixed two-decimal strings. */
  gross: string
  bvgAn: string
  bvgAg: string
  kinderzulagen: string
  ktgType: KtgValue
  status: EntryStatusTypeValue
  createdBy: number
  /** The payroll engine's breakdown as stored. */
  calculated: CalculatedType
  created: string
  updated: string
}

/** The table's row: the salary under `salary`, as the columns read it. */
export type SalaryRow = { salary: ApiSalary }

/** `POST /salaries/calculate/monthly` for one member; `months` left out means up to now. */
export type SalaryCalculateMonthly = { receiver: number; year: number; months?: number[] }

type SalaryCalculator = { year: number; ktgType: KtgValue; salaryConfiguration: UserSalaryAttributesType }

/** `POST /salaries/calculate/net-to-gross`. */
export type SalaryCalculateNetToGross = SalaryCalculator & { net: string }

/** `POST /salaries/calculate/rest-to-bonus`. */
export type SalaryCalculateRestToBonus = SalaryCalculator & { receiver: number; rest: string; saldo: string }

export type SalaryCalculatorResult = { result: string }
